import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * SWITCH free-gift auto-add — by Studio Lab.
 *
 * When a trigger product (custom.free_gift populated) is added to cart from its
 * PDP, this auto-adds the referenced gift product variant(s) via the AJAX
 * /cart/add route. Shopify Basic has no native scripts, so this is the theme
 * path chosen in Decision #27 (resolves PENDING C1).
 *
 * Pricing is NOT handled here: the gift is made free by an automatic
 * "Compre X, ganhe Y" discount configured in Shopify admin. This script only
 * adds the cart line.
 *
 * Behavior (matches the operator spec):
 *  - Add-only — removing the trigger does NOT remove the gift.
 *  - Idempotent — a gift variant already in the cart is not re-added, so repeat
 *    add-to-cart clicks never stack gifts.
 *  - PDP-scoped — only fires for the product whose free_gift data is rendered by
 *    blocks/switch-free-gift.liquid on its own page.
 *  - When custom.free_gift is empty the block (and this data) is absent, so the
 *    regular add-to-cart flow runs untouched.
 *
 * Horizon 4.x cart-event model (was @theme/events ThemeEvents.cartUpdate +
 * CartUpdateEvent in 3.x): cart updates are now StandardEvents.cartLinesUpdate
 * from '@shopify/events', carrying a deferred `event.promise` that resolves to
 * { cart, detail }. We listen for the trigger product's add, then add the gift
 * and dispatch our own CartLinesUpdateEvent (with morph sections) so the cart
 * drawer/page re-render — mirroring product-form.js.
 */

const config = (() => {
  const el = document.querySelector('[data-switch-free-gift]');
  if (!el || !(el instanceof HTMLElement)) return null;

  const triggerId = el.dataset.triggerId;
  const giftVariantIds = (el.dataset.giftVariants || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  if (!triggerId || giftVariantIds.length === 0) return null;
  return { triggerId, giftVariantIds };
})();

if (config) {
  let processing = false;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then(async ({ detail }) => {
        if (!detail || detail.didError) return;

        // Only react to a genuine add of THIS product from the product form
        // (excludes our own 'switch-free-gift' dispatch below → no re-entrancy loop).
        if (detail.source !== 'product-form-component') return;
        if (String(detail.productId) !== String(config.triggerId)) return;
        if (processing) return;
        processing = true;

        try {
          // Idempotency: only add gift variants not already in the cart.
          const liveCart = await (await fetch('/cart.js')).json();
          const present = new Set((liveCart.items || []).map((item) => String(item.variant_id)));
          const toAdd = config.giftVariantIds.filter((id) => !present.has(String(id)));
          if (toAdd.length === 0) return;

          // Section ids the cart components want morphed (so the drawer/page re-render).
          const sectionIds = [...document.querySelectorAll('cart-items-component')]
            .map((c) => /** @type {HTMLElement} */ (c).dataset.sectionId)
            .filter(Boolean);

          // Announce the update first (deferred promise resolves after the fetch),
          // mirroring product-form.js so listening cart components morph.
          const deferred = CartLinesUpdateEvent.createPromise();
          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: toAdd.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
              promise: deferred.promise,
            })
          );

          try {
            const res = await fetch(Theme.routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: toAdd.map((id) => ({ id: Number(id), quantity: 1 })),
                sections: sectionIds.join(','),
              }),
            });
            const addResponse = await res.json();
            // The /cart/add.js response lacks cart-level currency/totals, which
            // CartLinesUpdateEvent.createCartFromAjaxResponse needs (else the cart
            // listeners throw "Currency code is required with currency style").
            // Mirror product-form.js: build the event cart from the FULL cart
            // (/cart.js), and use the add response only for the morph sections.
            const fullCart = await (await fetch('/cart.js')).json();
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
              detail: {
                items: fullCart.items,
                sections: addResponse.sections,
                source: 'switch-free-gift',
                didError: false,
              },
            });
          } catch (addError) {
            deferred.reject(addError);
            throw addError;
          }
        } catch (error) {
          // Never block the primary purchase if the gift add fails.
          console.error('[switch-free-gift] auto-add failed:', error);
        } finally {
          processing = false;
        }
      })
      .catch(() => {
        /* trigger add itself failed/aborted — nothing to do */
      });
  });
}
